"use client";

import React from "react";
import { ArrowRight, Droplets, Flame, FlameKindling as FlameOff, CircleQuestionMark as HelpCircle, PhoneCall, PowerOff, Timer, Volume2, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
interface ProblemItem {
  id: number;
  title: string;
  cue: string;
  iconName: string;
}
const problems: ProblemItem[] = [{
  id: 1,
  title: "No Hot Water",
  cue: "Water stays cold from every tap, shower, and fixture.",
  iconName: "FlameOff"
}, {
  id: 2,
  title: "Water Heater Not Heating",
  cue: "Water turns lukewarm or fails to reach set temperature.",
  iconName: "Flame"
}, {
  id: 3,
  title: "Water Heater Leaking",
  cue: "Puddles, pooling, or steady drips near the base or valves.",
  iconName: "Droplets"
}, {
  id: 4,
  title: "Water Heater Making Noise",
  cue: "Popping, banging, humming, or screeching sounds during cycles.",
  iconName: "Volume2"
}, {
  id: 5,
  title: "Hot Water Runs Out Quickly",
  cue: "Warm water lasts only a few minutes before going ice cold.",
  iconName: "Timer"
}, {
  id: 6,
  title: "Water Heater Keeps Shutting Off",
  cue: "System trips reset switches, breakers, or shuts down repeatedly.",
  iconName: "PowerOff"
}, {
  id: 7,
  title: "Pilot Light Problem",
  cue: "Pilot flame will not light, turns yellow, or blows out constantly.",
  iconName: "Wrench"
}, {
  id: 8,
  title: "Not Sure What's Wrong",
  cue: "Discolored water, low pressure, foul smells, or general failure.",
  iconName: "HelpCircle"
}];
function ProblemIcon({
  name
}: {
  name: string;
}) {
  switch (name) {
    case "FlameOff":
      return <FlameOff className="size-6 text-primary" />;
    case "Flame":
      return <Flame className="size-6 text-primary" />;
    case "Droplets":
      return <Droplets className="size-6 text-primary" />;
    case "Volume2":
      return <Volume2 className="size-6 text-primary" />;
    case "Timer":
      return <Timer className="size-6 text-primary" />;
    case "PowerOff":
      return <PowerOff className="size-6 text-primary" />;
    case "Wrench":
      return <Wrench className="size-6 text-primary" />;
    case "HelpCircle":
    default:
      return <HelpCircle className="size-6 text-primary" />;
  }
}
export default function ProblemFinder() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.07
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="waterheaterproblemfinder" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            Fast Diagnostic Guide
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            What Water Heater Problem Are You Having?
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Select the symptom you are experiencing below to see common causes,
            recommended actions, and fast emergency repair options in Jonesboro.
          </p>
        </div>

        {/* Diagnostic 8-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {problems.map((problem, i) => <motion.div key={problem.id} data-index={i} variants={itemVariants} className="h-full">
              <Link to="/water-heater-problems/" className="group block h-full no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl">
                <Card className="h-full bg-card text-card-foreground border border-border transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg p-6 flex flex-col justify-between">
                  <div>
                    {/* Squircle Icon Pod */}
                    <div className="size-12 rounded-xl bg-secondary flex items-center justify-center mb-5 group-hover:bg-accent/20 transition-colors duration-200">
                      <ProblemIcon name={problem.iconName} />
                    </div>

                    {/* Problem Title & Cue */}
                    <h3 className="font-default text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-200">
                      {problem.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {problem.cue}
                    </p>
                  </div>

                  {/* Diagnostic Action Indicator */}
                  <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between text-xs font-semibold text-primary">
                    <span className="group-hover:underline">View Solutions</span>
                    <ArrowRight className="size-4 text-primary transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </Card>
              </Link>
            </motion.div>)}
        </motion.div>

        {/* Emergency Help Banner */}
        <div className="mt-12 md:mt-16 bg-secondary/70 border border-border rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-left">
            <div className="size-12 rounded-full bg-primary flex items-center justify-center shrink-0">
              <PhoneCall className="size-5 text-primary-foreground" />
            </div>
            <div>
              <h4 className="font-default text-base sm:text-lg font-bold text-foreground">
                Experiencing a major water heater leak or flooding?
              </h4>
              <p className="text-sm text-muted-foreground">
                Our Jonesboro emergency technicians are available 24/7 for immediate dispatch.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">Request Emergency Service</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/water-heater-problems/">Explore All Guides</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}